import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardService, shopService } from '../services';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewRes, shopsRes] = await Promise.all([
        dashboardService.getSuperAdminOverview(),
        shopService.getShops()
      ]);

      if (overviewRes.success) {
        setOverview(overviewRes);
      }

      if (shopsRes.success) {
        setShops(shopsRes.shops || []);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleShopStatus = async (shopId, currentStatus) => {
    try {
      await shopService.toggleShopStatus(shopId, !currentStatus);
      loadData(); // Reload data
    } catch (err) {
      console.error('Error toggling shop status:', err);
      alert('Failed to update shop status');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', maxWidth: '800px' }}>
        <h1 style={{ marginBottom: '15px' }}>Super Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => navigate('/register-shop')}
            style={{
              flex: 1,
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            + Register Shop
          </button>
          <button
            onClick={handleLogout}
            style={{
              flex: 1,
              padding: '10px 20px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {overview && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '30px', maxWidth: '800px' }}>
            <div style={{ padding: '20px', backgroundColor: '#9C27B0', color: 'white', borderRadius: '8px' }}>
              <h3>Total Shops</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{overview.summary.totalShops}</p>
              <small>Active: {overview.summary.activeShops}</small>
            </div>
            <div style={{ padding: '20px', backgroundColor: '#4CAF50', color: 'white', borderRadius: '8px' }}>
              <h3>Total Revenue</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>₹{overview.summary.totalRevenue.toFixed(2)}</p>
            </div>
            <div style={{ padding: '20px', backgroundColor: '#2196F3', color: 'white', borderRadius: '8px' }}>
              <h3>Total Orders</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{overview.summary.totalOrders}</p>
            </div>
            <div style={{ padding: '20px', backgroundColor: '#FF9800', color: 'white', borderRadius: '8px' }}>
              <h3>Avg Order Value</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold' }}>₹{overview.summary.averageOrderValue.toFixed(2)}</p>
            </div>
          </div>

          {/* Top Products - Commented out for now, can be re-enabled later
          <div style={{ marginBottom: '30px' }}>
            <h2>Top Selling Products (Nationwide)</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Product</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Quantity</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {overview.topProducts.slice(0, 10).map((product, index) => (
                  <tr key={index}>
                    <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{product.productName}</td>
                    <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                      {product.totalQuantity}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                      ₹{product.totalRevenue.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          */}
        </div>
      )}

      {/* Shops List */}
      <div style={{ marginTop: '30px' }}>
        <h2>All Shops</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Shop Info</th>
              <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd', width: '120px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((shop) => (
              <tr key={shop.shopId}>
                <td style={{ padding: '15px', borderBottom: '1px solid #ddd' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong style={{ fontSize: '16px', color: '#1976D2' }}>{shop.shopName}</strong>
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                    <strong>Owner:</strong> {shop.ownerName}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                    <strong>Mobile:</strong> {shop.mobile}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setSelectedShop(shop);
                        setShowQRModal(true);
                      }}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      📱 View QR
                    </button>
                    <button
                      onClick={() => toggleShopStatus(shop.shopId, shop.isActive)}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: shop.isActive ? '#f44336' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      {shop.isActive ? '🚫 Disable' : '✅ Enable'}
                    </button>
                  </div>
                </td>
                <td style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #ddd', verticalAlign: 'middle' }}>
                  <span style={{
                    padding: '6px 16px',
                    borderRadius: '16px',
                    backgroundColor: shop.isActive ? '#4CAF50' : '#f44336',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                  }}>
                    {shop.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* QR Code Modal */}
      {showQRModal && selectedShop && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>{selectedShop.shopName}</h2>
              <button
                onClick={() => {
                  setShowQRModal(false);
                  setSelectedShop(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p><strong>Owner:</strong> {selectedShop.ownerName}</p>
              <p><strong>Mobile:</strong> {selectedShop.mobile}</p>
              <p><strong>Address:</strong> {selectedShop.address}</p>
            </div>

            <div style={{ textAlign: 'center' }}>
              <h3>QR Code</h3>
              <img
                src={selectedShop.qrCodeUrl}
                alt="Shop QR Code"
                style={{ maxWidth: '300px', width: '100%', border: '2px solid #ccc', padding: '10px' }}
              />
              <div style={{ marginTop: '15px' }}>
                <a
                  href={selectedShop.qrCodeUrl}
                  download={`${selectedShop.shopName}-qr.png`}
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '4px'
                  }}
                >
                  Download QR Code
                </a>
              </div>
            </div>

            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>Order URL:</strong>
              <div style={{ fontSize: '14px', wordBreak: 'break-all', marginTop: '5px' }}>
                {selectedShop.orderUrl}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
